import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import ClientApiCredentialsSection from "./ClientApiCredentialsSection";

interface ClientIntegrationsTabProps {
  companyId: number;
  companyName?: string | null;
  channelId?: number | null;
  readOnly?: boolean;
}

export default function ClientIntegrationsTab({
  companyId,
  companyName,
  channelId,
  readOnly = false,
}: ClientIntegrationsTabProps) {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.clients.getI4HIntegration.useQuery(
    { companyId },
    { enabled: !!companyId },
  );

  const [enabled, setEnabled] = useState(false);
  const [externalCompanyCode, setExternalCompanyCode] = useState("");

  useEffect(() => {
    if (!data) return;

    setEnabled(Boolean(data.configured && data.status === "active"));
    setExternalCompanyCode(data.externalCompanyCode ? String(data.externalCompanyCode) : "");
  }, [data]);

  const isDirty = useMemo(() => {
    const initialEnabled = Boolean(data?.configured && data?.status === "active");
    const initialCode = data?.externalCompanyCode ? String(data.externalCompanyCode) : "";
    return enabled !== initialEnabled || externalCompanyCode !== initialCode;
  }, [data, enabled, externalCompanyCode]);

  const saveMutation = trpc.clients.upsertI4HIntegration.useMutation({
    onSuccess: async (result) => {
      await utils.clients.getI4HIntegration.invalidate({ companyId });
      toast.success(result.message);
    },
    onError: (error) => {
      toast.error(error.message || "Não foi possível salvar a integração I4H.");
    },
  });

  const handleSave = () => {
    const normalizedCode = externalCompanyCode.replace(/\D/g, "");

    if (enabled && !normalizedCode) {
      toast.error("Informe o código inteiro do cliente na I4H para salvar esta integração.");
      return;
    }

    saveMutation.mutate({
      companyId,
      enabled,
      externalCompanyCode: enabled ? Number(normalizedCode) : null,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-80" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-10 w-72" />
            <Skeleton className="h-28 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const updatedAtLabel = data?.updatedAt
    ? new Date(data.updatedAt).toLocaleString("pt-BR")
    : "Ainda não configurado";

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <CardTitle>Integrações</CardTitle>
              <CardDescription>
                Configure aqui apenas os dados técnicos que o sistema precisa para integrar este cliente.
              </CardDescription>
            </div>
            <Badge
              variant="secondary"
              className={enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}
            >
              {enabled ? "I4H configurada" : "I4H não configurada"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-900">Integração com a I4H</p>
              <p className="text-sm text-slate-600">
                O código da I4H fica salvo neste cadastro e será usado internamente quando o sistema gerar o arquivo de saída.
                Esse detalhe não aparece para o usuário operacional na Base de Vidas.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">{enabled ? "Ativa" : "Inativa"}</span>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                disabled={readOnly || saveMutation.isPending}
                aria-label="Ativar integração I4H"
              />
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
            <div className="space-y-2">
              <Label htmlFor="i4h-code">Código do cliente na I4H</Label>
              <Input
                id="i4h-code"
                inputMode="numeric"
                placeholder="Ex.: 1890002"
                value={externalCompanyCode}
                onChange={(event) => setExternalCompanyCode(event.target.value.replace(/\D/g, ""))}
                disabled={readOnly || !enabled || saveMutation.isPending}
              />
              <p className="text-xs text-slate-500">
                Use somente números inteiros. Esse código fica restrito ao cadastro do cliente e sustenta a integração de forma invisível para a operação.
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-900">Como esta configuração será usada</h3>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <p>
                  <strong>1.</strong> O time administrativo cadastra o código da I4H uma única vez em <strong>Cliente &gt; Integrações</strong>.
                </p>
                <p>
                  <strong>2.</strong> A Base de Vidas continua mostrando apenas empresa, arquivo, planos, validação e resultado operacional.
                </p>
                <p>
                  <strong>3.</strong> Quando o arquivo de saída for preparado para a I4H, o sistema resolve esse código em segundo plano.
                </p>
              </div>
            </div>
          </div>

          {enabled && !externalCompanyCode.trim() && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Ative a integração somente quando o código inteiro deste cliente na I4H estiver confirmado.
              </AlertDescription>
            </Alert>
          )}

          {readOnly && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Você está em modo somente leitura. A configuração da integração pode ser consultada, mas não editada.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-900">
                Cliente: {companyName || `#${companyId}`}
              </p>
              <p className="text-xs text-slate-500">Última atualização: {updatedAtLabel}</p>
            </div>
            <Button
              onClick={handleSave}
              disabled={readOnly || saveMutation.isPending || !isDirty || (enabled && !externalCompanyCode.trim())}
              className="gap-2"
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar integração
                </>
              )}
            </Button>
          </div>

          {enabled && data?.configured && data?.externalCompanyCode && (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              Código I4H pronto para uso interno na geração do arquivo de integração.
            </div>
          )}
        </CardContent>
      </Card>

      <ClientApiCredentialsSection companyId={companyId} channelId={channelId} readOnly={readOnly} />
    </div>
  );
}
